import {
  createStandingRuleRequestSchema,
  MAX_RULE_NAME_LENGTH,
  platformSchema,
  timeClassSchema,
  type Platform,
  type RuleGuard,
  type StandingRuleFilter,
  type StandingRuleResponse,
  type TimeClass,
} from '@gamble-chess/contracts';
import { useState, type JSX, type SubmitEvent } from 'react';

import { useCreateRule, useDeleteRule, useRules } from '../api/stakes.ts';
import { describeGuard, describePolicy, platformLabels, timeClassLabels } from '../format.ts';
import {
  initialStakePolicyDraft,
  parseStakePolicy,
  type StakePolicyDraft,
} from '../stakePolicyDraft.ts';
import { Alert } from '../ui/Alert.tsx';
import { Card } from '../ui/Card.tsx';
import { Entry, EntryList } from '../ui/EntryList.tsx';
import { Field } from '../ui/Field.tsx';
import { Loaded } from '../ui/Loaded.tsx';
import {
  fieldGridClass,
  hintClass,
  inputClass,
  secondaryButtonClass,
  submitClass,
} from '../ui/styles.ts';
import { RuleGuardFields } from './RuleGuardFields.tsx';
import { StakePolicyFields } from './StakePolicyFields.tsx';
import { TierExplanation } from './TierExplanation.tsx';
import { TimeZoneField } from './TimeZoneField.tsx';

const ANY = '';

function AddRuleForm({ timeZone }: { readonly timeZone: string }): JSX.Element {
  const createRule = useCreateRule();
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [timeClass, setTimeClass] = useState<TimeClass | null>(null);
  const [opponent, setOpponent] = useState('');
  const [ratedOnly, setRatedOnly] = useState(false);
  const [redeemWins, setRedeemWins] = useState(false);
  const [stake, setStake] = useState<StakePolicyDraft>(initialStakePolicyDraft);
  const [guards, setGuards] = useState<readonly RuleGuard[]>([]);
  const [invalid, setInvalid] = useState<string | undefined>(undefined);

  function submit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault();
    const policy = parseStakePolicy(stake);
    if (!policy.ok) {
      setInvalid(policy.error);
      return;
    }
    const parsed = createStandingRuleRequestSchema.safeParse({
      name,
      filter: {
        platform,
        timeClass,
        ratedOnly,
        opponent: opponent.trim() === '' ? null : opponent,
      },
      policy: policy.policy,
      redeemWins,
      guards,
    });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setInvalid(
        issue?.path.includes('opponent') === true
          ? `Opponent ${issue.message}.`
          : `Give the rule a name of up to ${String(MAX_RULE_NAME_LENGTH)} characters.`,
      );
      return;
    }
    setInvalid(undefined);
    createRule.mutate(parsed.data, {
      onSuccess: () => {
        setName('');
        setOpponent('');
        setGuards([]);
      },
    });
  }

  const error = invalid ?? (createRule.isError ? createRule.error.message : undefined);

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" aria-label="Add a standing rule">
      <div className={fieldGridClass}>
        <Field label="Rule name">
          {(id) => (
            <input
              id={id}
              value={name}
              maxLength={MAX_RULE_NAME_LENGTH}
              required
              onChange={(event) => {
                setName(event.target.value);
              }}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="Platform">
          {(id) => (
            <select
              id={id}
              value={platform ?? ANY}
              onChange={(event) => {
                const parsed = platformSchema.safeParse(event.target.value);
                setPlatform(parsed.success ? parsed.data : null);
              }}
              className={inputClass}
            >
              <option value={ANY}>Any platform</option>
              {platformSchema.options.map((option) => (
                <option key={option} value={option}>
                  {platformLabels[option]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Time control">
          {(id) => (
            <select
              id={id}
              value={timeClass ?? ANY}
              onChange={(event) => {
                const parsed = timeClassSchema.safeParse(event.target.value);
                setTimeClass(parsed.success ? parsed.data : null);
              }}
              className={inputClass}
            >
              <option value={ANY}>Any time control</option>
              {timeClassSchema.options.map((option) => (
                <option key={option} value={option}>
                  {timeClassLabels[option]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Only against (optional)">
          {(id) => (
            <input
              id={id}
              value={opponent}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => {
                setOpponent(event.target.value);
              }}
              className={inputClass}
            />
          )}
        </Field>
      </div>
      <div className="flex flex-wrap gap-x-6 text-sm">
        <label className="flex min-h-11 items-center gap-2">
          <input
            type="checkbox"
            checked={ratedOnly}
            onChange={(event) => {
              setRatedOnly(event.target.checked);
            }}
            className="size-4"
          />
          Rated games only
        </label>
        <label className="flex min-h-11 items-center gap-2">
          <input
            type="checkbox"
            checked={redeemWins}
            onChange={(event) => {
              setRedeemWins(event.target.checked);
            }}
            className="size-4"
          />
          Wins pay back open debt
        </label>
      </div>
      <StakePolicyFields value={stake} onChange={setStake} />
      <RuleGuardFields value={guards} onChange={setGuards} timeZone={timeZone} />
      <div>
        <button type="submit" disabled={createRule.isPending} className={submitClass}>
          {createRule.isPending ? 'Adding…' : 'Add rule'}
        </button>
      </div>
      {error === undefined ? null : <Alert>{error}</Alert>}
    </form>
  );
}

function describeFilter(filter: StandingRuleFilter): string {
  const parts = [
    filter.timeClass === null ? 'Losses' : `${timeClassLabels[filter.timeClass]} losses`,
    filter.platform === null ? 'on any platform' : `on ${platformLabels[filter.platform]}`,
  ];
  if (filter.opponent !== null) {
    parts.push(`against ${filter.opponent}`);
  }
  if (filter.ratedOnly) {
    parts.push('rated only');
  }
  return parts.join(' ');
}

function RuleEntry({ rule }: { readonly rule: StandingRuleResponse }): JSX.Element {
  const deleteRule = useDeleteRule();

  return (
    <Entry
      lead={rule.name}
      meta={
        <>
          <span className="text-sm">
            {describeFilter(rule.filter)} cost {describePolicy(rule.policy)}
            {rule.redeemWins ? '; wins pay it back' : ''}.
          </span>
          <TierExplanation policy={rule.policy} />
          {rule.guards.length === 0 ? null : (
            <ul aria-label={`Conditions of ${rule.name}`} className={hintClass}>
              {rule.guards.map((guard) => (
                <li key={describeGuard(guard)}>{describeGuard(guard)}.</li>
              ))}
            </ul>
          )}
          {deleteRule.isError ? <Alert>{deleteRule.error.message}</Alert> : null}
        </>
      }
      trailing={
        <button
          type="button"
          onClick={() => {
            deleteRule.mutate(rule.id);
          }}
          disabled={deleteRule.isPending}
          aria-label={`Delete rule ${rule.name}`}
          className={secondaryButtonClass}
        >
          {deleteRule.isPending ? 'Deleting…' : 'Delete'}
        </button>
      }
    />
  );
}

export interface StandingRulesProps {
  /** The signed-in user's zone: what the rules' days and times mean. */
  readonly timeZone: string;
}

/** The rules that charge your own losses, and the form that adds one. */
export function StandingRules({ timeZone }: StandingRulesProps): JSX.Element {
  const rules = useRules();

  return (
    <Card
      title="Standing rules"
      description="A rule charges the losses it matches from the moment it is added."
    >
      <TimeZoneField value={timeZone} />
      <AddRuleForm timeZone={timeZone} />
      <Loaded
        query={rules}
        loading="Loading your rules…"
        error="Your rules could not be loaded. Please reload the page."
      >
        {(list) =>
          list.length === 0 ? (
            <p className={hintClass}>No rules yet.</p>
          ) : (
            <EntryList label="Standing rules">
              {list.map((rule) => (
                <RuleEntry key={rule.id} rule={rule} />
              ))}
            </EntryList>
          )
        }
      </Loaded>
    </Card>
  );
}
